// Browser-side orchestrator for the 6-module RegDefence review pipeline.
// Running orchestration in the browser (rather than a single serverless
// function) sidesteps Vercel's 60s function timeout and gives the user
// real-time progress updates.

import { callAgent, callAgentJson, extractText } from './api.js';
import { aggregateUsage } from './cost.js';
import { buildFactExtractionMessage } from './prompts/factExtraction.js';
import { buildKycQaMessage } from './prompts/kycQa.js';
import { buildRedFlagsMessage } from './prompts/redFlags.js';
import { buildDefensibilityMessage } from './prompts/defensibility.js';
import { buildScoringMessage } from './prompts/scoring.js';
import { buildReportMessage } from './prompts/reportGenerator.js';

export const PIPELINE_STEPS = [
  { id: 'facts', label: 'Extract facts', model: 'haiku', tokens: 3000 },
  { id: 'qa', label: 'KYC QA assessment', model: 'sonnet', tokens: 4000 },
  { id: 'redFlags', label: 'Detect red flags', model: 'sonnet', tokens: 2500 },
  { id: 'defensibility', label: 'Assess defensibility', model: 'sonnet', tokens: 3000 },
  { id: 'scoring', label: 'Score & decide', model: 'haiku', tokens: 1500 },
  { id: 'report', label: 'Generate final report', model: 'sonnet', tokens: 4000 },
];

const MODELS = {
  haiku: 'claude-haiku-4-5-20251001',
  sonnet: 'claude-sonnet-4-20250514',
};

/**
 * Run the full review pipeline. Steps already present in `previous` are
 * skipped — this lets the UI resume after a per-step failure without
 * re-paying for work the model already completed.
 *
 * @param {{ text: string, fileName?: string, pages?: number|null, fileType?: string }} input
 * @param {{
 *   onProgress?: (update: { stepId: string, status: 'start'|'done'|'error'|'skipped', label: string, error?: string, durationMs?: number }) => void,
 *   onStepResult?: (stepId: string, partial: object) => void,
 *   previous?: { facts?: object, qaText?: string, redFlagsText?: string, defensibilityText?: string, scoring?: object, reportText?: string },
 * }} [opts]
 */
export async function runReview(input, opts = {}) {
  const { onProgress = () => {}, onStepResult = () => {}, previous = {} } = opts;
  const { text, fileName, pages, fileType } = input;
  const timings = {};
  const usageByStep = {}; // { stepId: { model, inputTokens, outputTokens } }
  const tag = (id) => PIPELINE_STEPS.find((s) => s.id === id)?.label || id;

  // Capture usage from a Claude API response (or a callAgentJson wrapper).
  // Returns the response/wrapper unchanged so it can be used in expressions.
  function capture(stepId, resp) {
    // resp shape from callAgent: { agent, model, content, usage, stop_reason }
    // resp shape from callAgentJson: { data, raw, attempts }
    // Look up usage on either; for callAgentJson we don't get usage directly,
    // so we record only model + estimate from token text length later.
    const model = resp?.model || resp?.raw?.model;
    const usage = resp?.usage || {};
    if (model || usage.input_tokens != null) {
      usageByStep[stepId] = {
        model: model || null,
        inputTokens: usage.input_tokens || 0,
        outputTokens: usage.output_tokens || 0,
      };
    }
    return resp;
  }

  async function step(id, fn) {
    const started = Date.now();
    onProgress({ stepId: id, status: 'start', label: tag(id) });
    try {
      const out = await fn();
      const durationMs = Date.now() - started;
      timings[id] = durationMs;
      onProgress({ stepId: id, status: 'done', label: tag(id), durationMs });
      return out;
    } catch (err) {
      onProgress({
        stepId: id,
        status: 'error',
        label: tag(id),
        error: err?.message || String(err),
      });
      throw err;
    }
  }

  function skip(id) {
    onProgress({ stepId: id, status: 'skipped', label: tag(id) });
  }

  // 1. Fact extraction (Haiku, JSON). Soft fail: warn + continue with {}
  // (downstream still has raw file text). Retried once on parse failure.
  let facts;
  if (previous.facts !== undefined) {
    facts = previous.facts;
    skip('facts');
  } else {
    try {
      const out = await step('facts', async () => {
        const wrapper = await callAgentJson(
          'fact-extractor',
          [{ role: 'user', content: buildFactExtractionMessage({ text, fileName, pages, fileType }) }],
          { model: MODELS.haiku, max_tokens: 3000 }
        );
        capture('facts', { model: MODELS.haiku, usage: wrapper.usage || {} });
        return wrapper;
      });
      facts = out.data || {};
    } catch (err) {
      console.warn('[orchestrator] fact extraction returned no parseable JSON; continuing with {}.', err.message);
      facts = {};
    }
    onStepResult('facts', { facts });
  }

  // 2. KYC QA (Sonnet, structured prose)
  let qaText;
  if (previous.qaText !== undefined) {
    qaText = previous.qaText;
    skip('qa');
  } else {
    const qaResp = await step('qa', () =>
      callAgent(
        'kyc-qa',
        [{ role: 'user', content: buildKycQaMessage({ facts, fileText: text, fileName }) }],
        { model: MODELS.sonnet, max_tokens: 4000 }
      ).then((r) => capture('qa', r))
    );
    qaText = extractText(qaResp);
    onStepResult('qa', { qaText });
  }

  // 3. Red flags (Sonnet)
  let redFlagsText;
  if (previous.redFlagsText !== undefined) {
    redFlagsText = previous.redFlagsText;
    skip('redFlags');
  } else {
    const redFlagsResp = await step('redFlags', () =>
      callAgent(
        'red-flag',
        [{ role: 'user', content: buildRedFlagsMessage({ facts, qaText, fileName }) }],
        { model: MODELS.sonnet, max_tokens: 2500 }
      ).then((r) => capture('redFlags', r))
    );
    redFlagsText = extractText(redFlagsResp);
    onStepResult('redFlags', { redFlagsText });
  }

  // 4. Defensibility (Sonnet)
  let defensibilityText;
  if (previous.defensibilityText !== undefined) {
    defensibilityText = previous.defensibilityText;
    skip('defensibility');
  } else {
    const defResp = await step('defensibility', () =>
      callAgent(
        'defensibility',
        [{ role: 'user', content: buildDefensibilityMessage({ facts, qaText, redFlagsText, fileName }) }],
        { model: MODELS.sonnet, max_tokens: 3000 }
      ).then((r) => capture('defensibility', r))
    );
    defensibilityText = extractText(defResp);
    onStepResult('defensibility', { defensibilityText });
  }

  // 5. Scoring (Haiku, JSON). Hard fail: surfaces real error to UI.
  let scoring;
  if (previous.scoring !== undefined) {
    scoring = previous.scoring;
    skip('scoring');
  } else {
    const scoreOut = await step('scoring', async () => {
      const wrapper = await callAgentJson(
        'scorer',
        [{ role: 'user', content: buildScoringMessage({ qaText, redFlagsText, defensibilityText, fileName }) }],
        { model: MODELS.haiku, max_tokens: 1500 }
      );
      capture('scoring', { model: MODELS.haiku, usage: wrapper.usage || {} });
      return wrapper;
    });
    scoring = scoreOut.data;
    onStepResult('scoring', { scoring });
  }

  // 6. Final report (Sonnet, long-form)
  let reportText;
  if (previous.reportText !== undefined) {
    reportText = previous.reportText;
    skip('report');
  } else {
    const reportResp = await step('report', () =>
      callAgent(
        'reporter',
        [{ role: 'user', content: buildReportMessage({ facts, qaText, redFlagsText, defensibilityText, scoring, fileName }) }],
        { model: MODELS.sonnet, max_tokens: 4000 }
      ).then((r) => capture('report', r))
    );
    reportText = extractText(reportResp);
    onStepResult('report', { reportText });
  }

  // Aggregate token + cost telemetry across all completed steps in this run.
  // (Skipped steps don't contribute — they were paid for in the prior run.)
  const usage = aggregateUsage(Object.values(usageByStep));

  return {
    facts, qaText, redFlagsText, defensibilityText, scoring, reportText,
    timings, usageByStep, usage,
  };
}

// Re-export so callAgentJson surfaces usage on the wrapper too.
// (The wrapper currently only returns { data, raw, attempts } — see api.js.)
